import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PublicPaymentPage.css';

// Create axios instance without auth interceptors for public routes
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api'
});

const PublicPaymentPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSecondPayment, setShowSecondPayment] = useState(false);
  const [agencyTerms, setAgencyTerms] = useState('');
  const [errors, setErrors] = useState({});
  
  const [primaryPayment, setPrimaryPayment] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    billingCountry: 'US'
  });
  
  const [secondaryPayment, setSecondaryPayment] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    amount: ''
  });
  
  const [contact, setContact] = useState({
    email: '',
    phone: ''
  });
  
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    fetchPaymentSession();
  }, [sessionId]);

  const fetchPaymentSession = async () => {
    try {
      const response = await publicAxios.get(`/public/payment-sessions/${sessionId}`);
      setSession(response.data);
      setAgencyTerms(response.data.agencyTerms || '');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching payment session:', err);
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate contact info
    if (!contact.email) newErrors.email = 'Email is required';
    if (!contact.phone) newErrors.phone = 'Phone is required';
    
    // Validate primary payment
    if (!primaryPayment.cardNumber) newErrors.cardNumber = 'Card number is required';
    if (!primaryPayment.cardName) newErrors.cardName = 'Cardholder name is required';
    if (!primaryPayment.expiryMonth) newErrors.expiryMonth = 'Expiry month is required';
    if (!primaryPayment.expiryYear) newErrors.expiryYear = 'Expiry year is required';
    if (!primaryPayment.cvv) newErrors.cvv = 'CVV is required';
    if (!primaryPayment.billingAddress) newErrors.billingAddress = 'Billing address is required';
    if (!primaryPayment.billingCity) newErrors.billingCity = 'City is required';
    if (!primaryPayment.billingState) newErrors.billingState = 'State is required';
    if (!primaryPayment.billingZip) newErrors.billingZip = 'ZIP code is required';
    
    // Validate secondary payment if shown
    if (showSecondPayment) {
      if (!secondaryPayment.cardNumber) newErrors.secondCardNumber = 'Second card number is required';
      if (!secondaryPayment.cardName) newErrors.secondCardName = 'Second cardholder name is required';
      if (!secondaryPayment.amount || parseFloat(secondaryPayment.amount) <= 0) {
        newErrors.secondAmount = 'Valid amount is required';
      }
      
      const totalAmount = parseFloat(primaryPayment.amount || session.amount) + parseFloat(secondaryPayment.amount || 0);
      if (totalAmount < session.amount) {
        newErrors.secondAmount = `Total must equal $${session.amount}`;
      }
    }
    
    if (!agreeToTerms) newErrors.terms = 'You must agree to the terms and conditions';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await publicAxios.post('/public/process-payment', {
        sessionId: sessionId,
        primaryPayment: primaryPayment,
        secondaryPayment: showSecondPayment ? secondaryPayment : null,
        contact: contact,
        agreeToTerms: agreeToTerms
      });
      
      if (response.data.success) {
        navigate(`/payment-success/${response.data.confirmationNumber}`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment processing failed. Please try again.');
      setSubmitting(false);
    }
  };

  const handlePrimaryAmountChange = (value) => {
    if (showSecondPayment) {
      const remaining = session.amount - parseFloat(value || 0);
      setSecondaryPayment(prev => ({ ...prev, amount: remaining > 0 ? remaining.toFixed(2) : '0' }));
    }
  };

  if (loading) return <div className="loading">Loading payment information...</div>;
  if (!session) return <div className="error">Payment session not found or expired</div>;

  return (
    <div className="public-payment-page">
      <div className="payment-header">
        <h1>Secure Payment</h1>
        <p>Complete your payment for Quote #{session.quote_number}</p>
        <div className="security-badge">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>Your payment information is secure and encrypted</span>
        </div>
      </div>

      <div className="payment-content">
        <div className="payment-summary">
          <h2>Payment Summary</h2>
          <div className="summary-line">
            <span>Trip:</span>
            <span>{session.quote_title}</span>
          </div>
          <div className="summary-line">
            <span>Travel Dates:</span>
            <span>
              {session.travelDates?.start ? new Date(session.travelDates.start).toLocaleDateString() : 'TBD'} - 
              {session.travelDates?.end ? new Date(session.travelDates.end).toLocaleDateString() : 'TBD'}
            </span>
          </div>
          <div className="summary-line">
            <span>Total Trip Cost:</span>
            <span>${parseFloat(session.total_amount).toFixed(2)}</span>
          </div>
          <div className="summary-line total">
            <span>Amount Due Now:</span>
            <span className="amount">${parseFloat(session.amount).toFixed(2)}</span>
          </div>
          <div className="summary-line">
            <span>Payment Type:</span>
            <span>{session.payment_type === 'deposit' ? 'Deposit' : 'Full Payment'}</span>
          </div>
          {session.payment_type === 'deposit' && (
            <div className="summary-note">
              Remaining balance of ${(parseFloat(session.total_amount) - parseFloat(session.amount)).toFixed(2)} will be due before travel.
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="payment-form">
          <h2>Contact Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Email Address*</label>
              <input
                type="email"
                value={contact.email}
                onChange={(e) => setContact({...contact, email: e.target.value})}
                className={errors.email ? 'error' : ''}
                required
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label>Phone Number*</label>
              <input
                type="tel"
                value={contact.phone}
                onChange={(e) => setContact({...contact, phone: e.target.value})}
                className={errors.phone ? 'error' : ''}
                required
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </div>

          <h2>Payment Method 1</h2>
          
          <div className="form-group">
            <label>Cardholder Name*</label>
            <input
              type="text"
              value={primaryPayment.cardName}
              onChange={(e) => setPrimaryPayment({...primaryPayment, cardName: e.target.value})}
              className={errors.cardName ? 'error' : ''}
              required
            />
            {errors.cardName && <span className="error-message">{errors.cardName}</span>}
          </div>

          <div className="form-group">
            <label>Card Number*</label>
            <input
              type="text"
              value={primaryPayment.cardNumber}
              onChange={(e) => setPrimaryPayment({...primaryPayment, cardNumber: e.target.value})}
              placeholder="1234 5678 9012 3456"
              className={errors.cardNumber ? 'error' : ''}
              required
            />
            {errors.cardNumber && <span className="error-message">{errors.cardNumber}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Expiry Date*</label>
              <div className="expiry-inputs">
                <input
                  type="text"
                  placeholder="MM"
                  maxLength="2"
                  value={primaryPayment.expiryMonth}
                  onChange={(e) => setPrimaryPayment({...primaryPayment, expiryMonth: e.target.value})}
                  className={errors.expiryMonth ? 'error' : ''}
                  required
                />
                <span>/</span>
                <input
                  type="text"
                  placeholder="YY"
                  maxLength="2"
                  value={primaryPayment.expiryYear}
                  onChange={(e) => setPrimaryPayment({...primaryPayment, expiryYear: e.target.value})}
                  className={errors.expiryYear ? 'error' : ''}
                  required
                />
              </div>
              {(errors.expiryMonth || errors.expiryYear) && 
                <span className="error-message">Valid expiry date required</span>}
            </div>
            <div className="form-group">
              <label>CVV*</label>
              <input
                type="text"
                maxLength="4"
                value={primaryPayment.cvv}
                onChange={(e) => setPrimaryPayment({...primaryPayment, cvv: e.target.value})}
                className={errors.cvv ? 'error' : ''}
                required
              />
              {errors.cvv && <span className="error-message">{errors.cvv}</span>}
            </div>
          </div>

          {showSecondPayment && (
            <div className="form-group">
              <label>Amount on this card*</label>
              <input
                type="number"
                step="0.01"
                value={primaryPayment.amount || session.amount}
                onChange={(e) => {
                  setPrimaryPayment({...primaryPayment, amount: e.target.value});
                  handlePrimaryAmountChange(e.target.value);
                }}
                required
              />
            </div>
          )}

          <h3>Billing Address</h3>
          
          <div className="form-group">
            <label>Street Address*</label>
            <input
              type="text"
              value={primaryPayment.billingAddress}
              onChange={(e) => setPrimaryPayment({...primaryPayment, billingAddress: e.target.value})}
              className={errors.billingAddress ? 'error' : ''}
              required
            />
            {errors.billingAddress && <span className="error-message">{errors.billingAddress}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City*</label>
              <input
                type="text"
                value={primaryPayment.billingCity}
                onChange={(e) => setPrimaryPayment({...primaryPayment, billingCity: e.target.value})}
                className={errors.billingCity ? 'error' : ''}
                required
              />
              {errors.billingCity && <span className="error-message">{errors.billingCity}</span>}
            </div>
            <div className="form-group">
              <label>State*</label>
              <input
                type="text"
                value={primaryPayment.billingState}
                onChange={(e) => setPrimaryPayment({...primaryPayment, billingState: e.target.value})}
                maxLength="2"
                className={errors.billingState ? 'error' : ''}
                required
              />
              {errors.billingState && <span className="error-message">{errors.billingState}</span>}
            </div>
            <div className="form-group">
              <label>ZIP Code*</label>
              <input
                type="text"
                value={primaryPayment.billingZip}
                onChange={(e) => setPrimaryPayment({...primaryPayment, billingZip: e.target.value})}
                className={errors.billingZip ? 'error' : ''}
                required
              />
              {errors.billingZip && <span className="error-message">{errors.billingZip}</span>}
            </div>
          </div>

          {!showSecondPayment && (
            <div className="add-payment-method">
              <button type="button" onClick={() => setShowSecondPayment(true)} className="link-button">
                + Add Second Payment Method
              </button>
            </div>
          )}

          {showSecondPayment && (
            <div className="second-payment-section">
              <div className="section-header">
                <h2>Payment Method 2</h2>
                <button type="button" onClick={() => setShowSecondPayment(false)} className="remove-button">
                  Remove
                </button>
              </div>

              <div className="form-group">
                <label>Amount on second card*</label>
                <input
                  type="number"
                  step="0.01"
                  value={secondaryPayment.amount}
                  onChange={(e) => {
                    setSecondaryPayment({...secondaryPayment, amount: e.target.value});
                    const primaryAmount = session.amount - parseFloat(e.target.value || 0);
                    setPrimaryPayment(prev => ({ ...prev, amount: primaryAmount > 0 ? primaryAmount.toFixed(2) : '0' }));
                  }}
                  className={errors.secondAmount ? 'error' : ''}
                  required
                />
                {errors.secondAmount && <span className="error-message">{errors.secondAmount}</span>}
              </div>

              <div className="form-group">
                <label>Cardholder Name*</label>
                <input
                  type="text"
                  value={secondaryPayment.cardName}
                  onChange={(e) => setSecondaryPayment({...secondaryPayment, cardName: e.target.value})}
                  className={errors.secondCardName ? 'error' : ''}
                  required
                />
                {errors.secondCardName && <span className="error-message">{errors.secondCardName}</span>}
              </div>

              <div className="form-group">
                <label>Card Number*</label>
                <input
                  type="text"
                  value={secondaryPayment.cardNumber}
                  onChange={(e) => setSecondaryPayment({...secondaryPayment, cardNumber: e.target.value})}
                  placeholder="1234 5678 9012 3456"
                  className={errors.secondCardNumber ? 'error' : ''}
                  required
                />
                {errors.secondCardNumber && <span className="error-message">{errors.secondCardNumber}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Expiry Date*</label>
                  <div className="expiry-inputs">
                    <input
                      type="text"
                      placeholder="MM"
                      maxLength="2"
                      value={secondaryPayment.expiryMonth}
                      onChange={(e) => setSecondaryPayment({...secondaryPayment, expiryMonth: e.target.value})}
                      required
                    />
                    <span>/</span>
                    <input
                      type="text"
                      placeholder="YY"
                      maxLength="2"
                      value={secondaryPayment.expiryYear}
                      onChange={(e) => setSecondaryPayment({...secondaryPayment, expiryYear: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>CVV*</label>
                  <input
                    type="text"
                    maxLength="4"
                    value={secondaryPayment.cvv}
                    onChange={(e) => setSecondaryPayment({...secondaryPayment, cvv: e.target.value})}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {agencyTerms && (
            <div className="terms-section">
              <h3>Travel Agency Terms & Conditions</h3>
              <div className="terms-content" dangerouslySetInnerHTML={{ __html: agencyTerms }} />
            </div>
          )}

          <div className="agreement-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
              />
              <span>I agree to the terms and conditions*</span>
            </label>
            {errors.terms && <span className="error-message">{errors.terms}</span>}
          </div>

          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting ? 'Processing...' : `Pay $${parseFloat(session.amount).toFixed(2)}`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PublicPaymentPage;
